import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Pencil } from 'lucide-react';
import { useUpdateVersion } from '@/services/queries';
import { ApiError } from '@/services/api/client';
import type { SoftwareVersion, UpdateVersionRequest } from '@/types';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { splitFormErrors, toFormErrors } from '@/shared/lib/forms';

const SEMVER = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/;

const schema = Yup.object({
  releaseNotes: Yup.string().max(10000),
  minSupportedVersion: Yup.string().matches(SEMVER, {
    message: 'Use a semantic version, e.g. 0.0.1',
    excludeEmptyString: true,
  }),
});

/**
 * A published build's policy: its notes, whether it is required, and the
 * oldest version still allowed to run.
 *
 * The installer, its hash and the version number are not on this form because
 * they never change - a desktop that verified one file must not be handed
 * another under the same name. Builds registered by CI arrive without notes;
 * this is where a person adds them.
 *
 * Only what changed is sent, so saving the notes never rewrites "required".
 */
export function EditVersionDialog({ version }: { version: SoftwareVersion }) {
  const [open, setOpen] = useState(false);
  const update = useUpdateVersion();

  const initial = {
    releaseNotes: version.releaseNotes ?? '',
    mandatory: version.mandatory,
    minSupportedVersion: version.minSupportedVersion ?? '',
  };

  const form = useFormik({
    initialValues: initial,
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async (values, helpers) => {
      const body: UpdateVersionRequest = {};
      const notes = values.releaseNotes.trim();
      if (notes !== initial.releaseNotes.trim()) body.releaseNotes = notes || null;
      if (values.mandatory !== initial.mandatory) body.mandatory = values.mandatory;
      const floor = values.minSupportedVersion.trim();
      if (floor !== initial.minSupportedVersion) body.minSupportedVersion = floor || null;
      if (Object.keys(body).length === 0) {
        setOpen(false);
        return;
      }
      try {
        await update.mutateAsync({ versionId: version.id, body });
        setOpen(false);
      } catch (error) {
        if (error instanceof ApiError && error.fieldErrors) {
          const { placed } = splitFormErrors(toFormErrors(error.fieldErrors), form.values);
          helpers.setErrors(placed);
        }
      }
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) form.resetForm();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" aria-label={`Edit ${version.versionNumber}`}>
          <Pencil className="size-4" />
          Edit
        </Button>
      </DialogTrigger>

      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <form onSubmit={form.handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit {version.versionNumber}</DialogTitle>
            <DialogDescription>
              Notes and whether this build is required. The installer and its hash cannot change -
              release a newer version for a different build.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-notes">Release notes</Label>
              <textarea
                id="edit-notes"
                name="releaseNotes"
                rows={5}
                value={form.values.releaseNotes}
                onChange={form.handleChange}
                placeholder="What changed, in the words a lab would use."
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
              />
              <p className="text-xs text-muted-foreground">
                Shown in the desktop&rsquo;s update prompt, its Settings &gt; Updates page and the
                customer portal.
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-floor">Minimum supported version (optional)</Label>
              <Input
                id="edit-floor"
                name="minSupportedVersion"
                value={form.values.minSupportedVersion}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                placeholder="0.0.1"
                aria-invalid={Boolean(
                  form.touched.minSupportedVersion && form.errors.minSupportedVersion,
                )}
              />
              {form.touched.minSupportedVersion && form.errors.minSupportedVersion && (
                <p className="text-sm text-destructive">{form.errors.minSupportedVersion}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Anything older must update before it can be used. Empty: no floor.
              </p>
            </div>

            <div className="space-y-2 rounded-md border border-border p-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  name="mandatory"
                  checked={form.values.mandatory}
                  onChange={form.handleChange}
                  className="size-4"
                />
                Mandatory
              </label>
              <p className="text-xs text-muted-foreground">
                Stops NμDent on every older machine until it updates, rather than offering the
                update. Check adoption below before turning this on.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={form.isSubmitting || update.isPending}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
